import env from '@/config/env';
import {
  normalizeNumericEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { formatFileSize } from '@/utils/infrastructure';
import { request } from '@/utils/infrastructure/communication';
import { resolveFileTypeClassification } from '@/utils/infrastructure/file-system';
import { AUTOMY_ACCENT } from '@/utils/infrastructure/utilities/colors';

import {
  ASSET_SLOTS,
  BRANDING_FORM_FIELDS,
  FETCH_ERROR,
  IMAGE_ASSET_SLOTS,
  MAIN_COLOR_PATTERN,
  WhiteLabelCustomDomainStatus,
  WhiteLabelSource,
} from './constants';

export const unwrapEntity = (response) => {
  if (response == null || response === '') return null;
  if (typeof response !== 'object') return null;
  return response.data ?? response.item ?? response.whiteLabel ?? response;
};

/** Detect GET 204 / empty local config (create mode). */
export const isEmptyLocalResponse = (result) => {
  if (result == null || result === '') return true;
  if (typeof result === 'object' && Object.keys(result).length === 0) return true;
  return false;
};

export const getHttpStatus = (error) =>
  error?.status || error?.response?.status || error?.data?.status || null;

export const resolveFetchErrorCode = (error) =>
  getHttpStatus(error) === 403 ? FETCH_ERROR.FORBIDDEN : FETCH_ERROR.LOAD_FAILED;

/** True when GET local failed and there is no entity to edit — do not enter create mode. */
export const isLocalLoadBlocked = (local) => Boolean(local?.error && !local?.data);

export const getSectionErrorId = (error) =>
  error === FETCH_ERROR.FORBIDDEN
    ? 'whiteLabel.errors.forbidden'
    : 'whiteLabel.errors.loadFailed';

const GENERIC_CONTENT_TYPES = ['application/octet-stream', 'binary/octet-stream'];

export const isGenericContentType = (contentType) => {
  const type = String(contentType || '').toLowerCase();
  return !type || GENERIC_CONTENT_TYPES.includes(type);
};

/** API 400 body `{ message }` — show as-is, never a generic substitute. */
export const getApiErrorMessage = (error) => {
  const fromData = error?.data?.message;
  if (typeof fromData === 'string' && fromData.trim()) return fromData.trim();
  const fromNested = error?.data?.error?.message || error?.response?.data?.message;
  if (typeof fromNested === 'string' && fromNested.trim()) return fromNested.trim();
  if (typeof error?.message === 'string' && error.message.trim()) return error.message.trim();
  return null;
};

/**
 * PUT 400 `validationErrors[]` uses `propertyName` + `errorMessage`.
 * `fieldErrorMessage` expects `field` + `errorMessages[]`.
 */
export const mapWhiteLabelValidationErrors = (validationErrors = []) => {
  if (!Array.isArray(validationErrors)) return [];
  return validationErrors
    .map((item) => {
      const rawField = item?.propertyName || item?.field;
      if (!rawField) return null;
      const field = `${String(rawField).charAt(0).toLowerCase()}${String(rawField).slice(1)}`;
      const message = item.errorMessage;
      const messages = Array.isArray(item.errorMessages)
        ? item.errorMessages
        : message
        ? [message]
        : [];
      if (!messages.length) return null;
      return { field, errorMessages: messages };
    })
    .filter(Boolean);
};

export const extractValidationErrors = (error) =>
  error?.validationErrors ||
  error?.data?.validationErrors ||
  error?.response?.data?.validationErrors ||
  [];

export const toBooleanValue = (value, fallback = true) => {
  if (value == null || value === '') return fallback;
  if (typeof value === 'object') {
    const raw = value.value ?? value.key;
    if (raw == null || raw === '') return fallback;
    return raw === true || raw === 'true';
  }
  if (value === false || value === 'false' || value === 0 || value === '0') return false;
  return value === true || value === 'true' || value === 1 || value === '1';
};

const isAttachmentItem = (value) =>
  value != null &&
  typeof value === 'object' &&
  (value.id || value.documentUrl || value.documentTemporaryUrl || value.fileName);

export const toAttachmentValue = (value) => {
  if (!isAttachmentItem(value)) return null;
  return value;
};

const isHttpUrl = (value) => typeof value === 'string' && /^https?:\/\//i.test(value);

/**
 * PUT JsonAttachmentItem: new files send `documentTemporaryUrl` (same as
 * Documents / Media / FormBuilder). Already-persisted slots send `id` +
 * `documentUrl`. Never send `content` or in-memory `downloadUrl`.
 */
export const toPutAttachment = (value) => {
  const attachment = toAttachmentValue(value);
  if (!attachment) return null;

  const documentTemporaryUrl = isHttpUrl(attachment.documentTemporaryUrl)
    ? attachment.documentTemporaryUrl
    : null;

  if (documentTemporaryUrl) {
    return {
      fileName: attachment.fileName,
      contentType: attachment.contentType,
      size: attachment.size,
      fileTypeClassification: attachment.fileTypeClassification,
      documentTemporaryUrl,
    };
  }

  if (!attachment.id) return null;

  return {
    id: attachment.id,
    fileName: attachment.fileName,
    contentType: attachment.contentType,
    size: attachment.size,
    fileTypeClassification: attachment.fileTypeClassification,
    documentUrl: attachment.documentUrl,
  };
};

export const getFileExtension = (fileName) => {
  if (!fileName || typeof fileName !== 'string') return '';
  const parts = fileName.trim().split('.');
  if (parts.length < 2) return '';
  return parts.pop().toLowerCase();
};

export const isImageAssetSlot = (slot) => IMAGE_ASSET_SLOTS.includes(slot);

export const formatAllowedExtensions = (requirement) => {
  const extensions = requirement?.allowedExtensions;
  if (!Array.isArray(extensions) || !extensions.length) return '';
  return extensions
    .map((ext) => String(ext || '').replace(/^\./, '').toUpperCase())
    .filter(Boolean)
    .join(', ');
};

export const buildAssetAccept = (requirement) => {
  if (!requirement) return undefined;
  const types = Array.isArray(requirement.allowedContentTypes)
    ? requirement.allowedContentTypes
    : [];
  const extensions = Array.isArray(requirement.allowedExtensions)
    ? requirement.allowedExtensions.map((ext) => {
        const clean = String(ext || '').replace(/^\./, '');
        return clean ? `.${clean}` : '';
      })
    : [];
  const accept = [...types, ...extensions].filter(Boolean).join(',');
  return accept || undefined;
};

export const guessContentType = (file, requirement) => {
  if (file?.type) return file.type;
  const types = requirement?.allowedContentTypes;
  if (Array.isArray(types) && types.length) return types[0];
  return 'application/octet-stream';
};

/**
 * Azure Blob rejects non-ASCII in request headers. Keep `filename=` ASCII-only;
 * put the real name in RFC 5987 `filename*` (same as FormBuilder / Documents).
 */
const buildBlobContentDisposition = (fileName) => {
  const raw = String(fileName || 'file');
  const ascii = raw.replace(/[^\x20-\x7E]/g, '_').replace(/"/g, '') || 'file';
  return `attachment; filename="${ascii}"; filename*=UTF-8''${encodeURIComponent(raw)}`;
};

/**
 * Upload to temporary Azure blob (SAS) and return a JsonAttachmentItem with
 * `documentTemporaryUrl`. Same flow as Documents / FormBuilder: the PUT of
 * branding sends that URL; the file never travels as base64.
 */
export async function uploadTemporaryAttachment(file, requirement) {
  const raw = file?.originFileObj || file;
  const fileName = file?.name || raw?.name;
  const contentType = guessContentType(raw, requirement);
  const size = raw?.size || 0;
  const fileTypeClassification =
    requirement?.fileTypeClassification ?? resolveFileTypeClassification(contentType, fileName);

  const data = await request(
    `/${env.AZURE_SAS_TOKEN_URI}?fileName=${encodeURIComponent(fileName)}`,
  );
  await request.put(data.sasUri, {
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'x-ms-blob-content-type': contentType,
      'x-ms-blob-content-disposition': buildBlobContentDisposition(fileName),
      'Content-Type': contentType,
      'Content-length': size,
    },
    body: raw,
  });

  return {
    fileName,
    contentType,
    size,
    fileTypeClassification,
    documentTemporaryUrl: data.sasUri,
  };
}

/**
 * Client-side gate from GET asset-requirements. API remains the authority;
 * 400 `message` is shown as-is when this check is skipped or disagrees.
 */
export const validateAssetFile = (file, requirement, { formatMessage, slotLabel } = {}) => {
  const translate = (id, values) =>
    formatMessage ? formatMessage({ id }, values) : id;
  const slot = slotLabel || '';
  const allowed = formatAllowedExtensions(requirement);

  if (!file) {
    return translate('whiteLabel.validation.asset.required', { slot });
  }

  const fileName = file.name || '';
  if (!fileName.trim()) {
    return translate('whiteLabel.validation.asset.noName', { slot });
  }

  if (!file.size) {
    return translate('whiteLabel.validation.asset.empty', { slot });
  }

  const extension = getFileExtension(fileName);
  const contentType = String(file.type || '').toLowerCase();
  const isSvg = extension === 'svg' || contentType === 'image/svg+xml';

  if (isSvg) {
    return translate('whiteLabel.validation.asset.svg', { slot, allowed });
  }

  const allowedExtensions = Array.isArray(requirement?.allowedExtensions)
    ? requirement.allowedExtensions.map((ext) => String(ext || '').replace(/^\./, '').toLowerCase())
    : [];
  if (allowedExtensions.length && extension && !allowedExtensions.includes(extension)) {
    return translate('whiteLabel.validation.asset.extension', {
      slot,
      extension: `.${extension}`,
      allowed,
    });
  }

  const allowedTypes = Array.isArray(requirement?.allowedContentTypes)
    ? requirement.allowedContentTypes.map((type) => String(type || '').toLowerCase())
    : [];
  if (contentType && allowedTypes.length && !allowedTypes.includes(contentType)) {
    const extensionAllowed =
      allowedExtensions.length && extension && allowedExtensions.includes(extension);
    if (!(extensionAllowed && isGenericContentType(contentType))) {
      return translate('whiteLabel.validation.asset.mime', { slot, allowed });
    }
  }

  const maxBytes = requirement?.maxBytes;
  if (maxBytes && file.size > maxBytes) {
    return translate('whiteLabel.validation.asset.size', {
      slot,
      maxSize: formatFileSize(maxBytes) || String(maxBytes),
    });
  }

  return null;
};

export const formatAssetHint = (requirement, { formatMessage, isFont } = {}) => {
  if (!requirement || !formatMessage) return null;
  const extensions = formatAllowedExtensions(requirement);
  const maxSize = requirement.maxBytes ? formatFileSize(requirement.maxBytes) : '';
  const id = isFont ? 'whiteLabel.form.assetHint.font' : 'whiteLabel.form.assetHint';
  return formatMessage({ id }, { extensions, maxSize });
};

/** Map API local entity → Ant Design form values (full JsonAttachmentItem per slot). */
export const mapLocalToFormValues = (local) => {
  if (!local) {
    return {
      isActive: true,
      applyToChildren: false,
      brandName: '',
      mainColor: AUTOMY_ACCENT,
      supportEmail: '',
    };
  }

  const values = {
    isActive: toBooleanValue(local.isActive, true),
    applyToChildren: !!local.applyToChildren,
    brandName: local.brandName ?? '',
    mainColor: local.mainColor ?? AUTOMY_ACCENT,
    supportEmail: local.supportEmail ?? '',
  };

  ASSET_SLOTS.forEach((slot) => {
    values[slot] = toAttachmentValue(local[slot]) || undefined;
  });

  return values;
};

/** Build PUT body from form values. Omit `version` when creating. Never send customDomain. */
export const buildBrandingPayload = (values, { version, isCreate, original } = {}) => {
  const brandName = (values.brandName ?? '').trim();
  const mainColor = String(values.mainColor ?? '')
    .trim()
    .toUpperCase();
  const supportEmail = (values.supportEmail ?? '').trim();

  const payload = {
    isActive: toBooleanValue(values.isActive, true),
    applyToChildren: !!values.applyToChildren,
    brandName: brandName || null,
    mainColor: MAIN_COLOR_PATTERN.test(mainColor) ? mainColor : null,
    supportEmail: supportEmail || null,
  };

  ASSET_SLOTS.forEach((slot) => {
    if (values[slot] === null) {
      payload[slot] = null;
      return;
    }

    const fromForm = toPutAttachment(values[slot]);
    if (fromForm) {
      payload[slot] = fromForm;
      return;
    }

    if (isCreate) {
      payload[slot] = null;
      return;
    }

    const fromOriginal = toPutAttachment(original?.[slot]);
    if (fromOriginal) {
      payload[slot] = fromOriginal;
    }
  });

  if (!isCreate && version != null) {
    payload.version = version;
  }

  return payload;
};

export const resolveAttachmentPreviewUrl = (attachment) => {
  if (!isAttachmentItem(attachment)) return null;
  const url = attachment.downloadUrl || attachment.documentTemporaryUrl;
  return typeof url === 'string' && url.trim() ? url.trim() : null;
};

export const resolveEffectiveAssetUrl = (entity, urlKey) => {
  if (!entity) return null;
  const url = entity[urlKey];
  if (typeof url === 'string' && url.trim()) return url.trim();
  return null;
};

export const normalizeWhiteLabelSource = (raw, fallback = WhiteLabelSource.Default) =>
  normalizeNumericEnumValue(WhiteLabelSource, raw, fallback);

export const normalizeWhiteLabelCustomDomainStatus = (
  raw,
  fallback = WhiteLabelCustomDomainStatus.NotConfigured,
) => normalizeNumericEnumValue(WhiteLabelCustomDomainStatus, raw, fallback);

export const resolveEffectiveSource = (effective, local) => {
  if (!effective) return WhiteLabelSource.Default;

  const raw =
    effective.effectiveSource ??
    effective.source ??
    effective.brandingSource ??
    effective.resolutionSource;

  const parsed = parseOptionalNumericEnumFromApi(WhiteLabelSource, raw);
  if (parsed !== undefined) return parsed;

  if (effective.isDefault || effective.usesDefault) return WhiteLabelSource.Default;

  if (local?.id && effective.companyId && local.companyId === effective.companyId) {
    return WhiteLabelSource.Own;
  }
  if (
    effective.targetCompanyId &&
    effective.sourceCompanyId &&
    String(effective.targetCompanyId).toLowerCase() ===
      String(effective.sourceCompanyId).toLowerCase()
  ) {
    return WhiteLabelSource.Own;
  }
  if (effective.isInherited || effective.inheritedFromCompanyId) {
    return WhiteLabelSource.Inherited;
  }
  if (effective.brandName || effective.mainColor || effective.logoUrl) {
    return local ? WhiteLabelSource.Own : WhiteLabelSource.Inherited;
  }
  return WhiteLabelSource.Default;
};

export const normalizeHostname = (value) => {
  if (!value || typeof value !== 'string') return '';
  let host = value.trim().toLowerCase();
  host = host.replace(/^https?:\/\//, '');
  host = host.split('/')[0];
  host = host.split('?')[0];
  host = host.split('#')[0];
  host = host.replace(/:\d+$/, '');
  return host;
};

export const isReservedAutomyDomain = (hostname) => {
  const host = normalizeHostname(hostname);
  return host === 'automy.global' || host.endsWith('.automy.global');
};

export const isValidCustomHostname = (value) => {
  const host = normalizeHostname(value);
  if (!host || host.includes(' ') || host.includes('/') || host.includes(':')) return false;
  if (isReservedAutomyDomain(host)) return false;
  // labels of 1–63 chars, no leading/trailing hyphen, at least one dot
  if (!/^[a-z0-9.-]+$/i.test(host)) return false;
  if (host.startsWith('.') || host.endsWith('.') || host.includes('..')) return false;
  const labels = host.split('.');
  if (labels.length < 2) return false;
  return labels.every((label) => /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/i.test(label));
};

export const getDomainStatusLabelId = (status) => {
  const normalized = normalizeWhiteLabelCustomDomainStatus(status);
  switch (normalized) {
    case WhiteLabelCustomDomainStatus.PendingVerification:
      return 'whiteLabel.domain.status.pending';
    case WhiteLabelCustomDomainStatus.Verified:
      return 'whiteLabel.domain.status.verified';
    case WhiteLabelCustomDomainStatus.Active:
      return 'whiteLabel.domain.status.active';
    case WhiteLabelCustomDomainStatus.Disabled:
      return 'whiteLabel.domain.status.disabled';
    default:
      return 'whiteLabel.domain.status.notConfigured';
  }
};

/** Maps domain status → `@/components/Badge` status (same pattern as AI Billing). */
export const getDomainStatusBadgeStatus = (status) => {
  const normalized = normalizeWhiteLabelCustomDomainStatus(status);
  switch (normalized) {
    case WhiteLabelCustomDomainStatus.PendingVerification:
      return 'pending';
    case WhiteLabelCustomDomainStatus.Verified:
      return 'info';
    case WhiteLabelCustomDomainStatus.Active:
      return 'success';
    case WhiteLabelCustomDomainStatus.Disabled:
      return 'disabled';
    default:
      return 'default';
  }
};

export const getEffectiveSourceLabelId = (source) => {
  const normalized = normalizeWhiteLabelSource(source);
  switch (normalized) {
    case WhiteLabelSource.Own:
      return 'whiteLabel.effective.source.own';
    case WhiteLabelSource.Inherited:
      return 'whiteLabel.effective.source.inherited';
    default:
      return 'whiteLabel.effective.defaultBrand';
  }
};

export const getEffectiveSourceBadgeStatus = (source) => {
  const normalized = normalizeWhiteLabelSource(source);
  switch (normalized) {
    case WhiteLabelSource.Own:
      return 'info';
    case WhiteLabelSource.Inherited:
      return 'pending';
    default:
      return 'default';
  }
};

export const mergeDomainResponse = (local, response) => {
  if (!response) return local;
  return {
    ...(local || {}),
    ...response,
    version: response.version ?? local?.version,
  };
};

const snapshotAssetId = (value) => {
  const attachment = toAttachmentValue(value);
  if (!attachment) return null;
  return attachment.documentTemporaryUrl || attachment.id || null;
};

export const toBrandingDraft = (values) => {
  if (!values) return null;
  const draft = {
    isActive: toBooleanValue(values.isActive, true),
    applyToChildren: !!values.applyToChildren,
    brandName: values.brandName ?? '',
    mainColor: values.mainColor ?? '',
    supportEmail: values.supportEmail ?? '',
  };
  ASSET_SLOTS.forEach((slot) => {
    draft[slot] = toAttachmentValue(values[slot]);
  });
  return draft;
};

export const snapshotBrandingValues = (values) => {
  const snapshot = {};
  BRANDING_FORM_FIELDS.forEach((key) => {
    if (ASSET_SLOTS.includes(key)) {
      snapshot[key] = snapshotAssetId(values?.[key]);
    } else if (key === 'brandName' || key === 'supportEmail') {
      snapshot[key] = (values?.[key] ?? '').trim();
    } else if (key === 'mainColor') {
      snapshot[key] = String(values?.[key] ?? '')
        .trim()
        .toUpperCase();
    } else if (key === 'applyToChildren') {
      snapshot[key] = !!values?.[key];
    } else if (key === 'isActive') {
      snapshot[key] = toBooleanValue(values?.[key], true);
    } else {
      snapshot[key] = values?.[key];
    }
  });
  return snapshot;
};

export const isBrandingDirty = (baseline, current) => {
  if (!baseline) return false;
  const left = snapshotBrandingValues(baseline);
  const right = snapshotBrandingValues(current);
  return BRANDING_FORM_FIELDS.some((key) => left[key] !== right[key]);
};
